import { Fragment } from 'react'
import { ClientStatus, CLIENT_STATUSES } from '../services/clientService'

interface ClientStatusStepperProps {
  status: ClientStatus
  onChange: (status: ClientStatus) => void
}

const statusLabel = (s: ClientStatus) => s.charAt(0).toUpperCase() + s.slice(1)

// Deliberately sequential: clicking a step only ever moves status forward or
// backward by exactly one, via the buttons below the track — the circles
// themselves are just a read-only progress display, not jump targets. This
// stops a status from skipping straight from "submitted" to "completed".
export const ClientStatusStepper: React.FC<ClientStatusStepperProps> = ({ status, onChange }) => {
  const safeStatus = CLIENT_STATUSES.includes(status) ? status : CLIENT_STATUSES[0]
  const currentIndex = CLIENT_STATUSES.indexOf(safeStatus)
  const isFinal = currentIndex === CLIENT_STATUSES.length - 1
  const nextStatus = isFinal ? null : CLIENT_STATUSES[currentIndex + 1]
  const prevStatus = currentIndex === 0 ? null : CLIENT_STATUSES[currentIndex - 1]

  return (
    <div>
      <div className="flex items-center">
        {CLIENT_STATUSES.map((s, i) => {
          const isDone = i < currentIndex
          const isCurrent = i === currentIndex
          const isLast = i === CLIENT_STATUSES.length - 1

          return (
            <Fragment key={s}>
              <div
                aria-current={isCurrent ? 'step' : undefined}
                className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  isDone
                    ? 'bg-primary text-white'
                    : isCurrent
                    ? 'bg-white text-primary border-2 border-primary ring-4 ring-primary/15'
                    : 'bg-white text-gray-400 border-2 border-gray-300'
                }`}
              >
                {isDone ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-1 ${isDone ? 'bg-primary' : 'bg-gray-200'}`} />
              )}
            </Fragment>
          )
        })}
      </div>

      <div className="flex mt-1.5">
        {CLIENT_STATUSES.map((s, i) => {
          const isCurrent = i === currentIndex
          const isFirst = i === 0
          const isLast = i === CLIENT_STATUSES.length - 1
          // The end labels sit beside their bullet rather than under it —
          // "Submitted" to the left of the first circle, "Completed" to the
          // right of the last — so neither one centers under a circle that's
          // flush against the track's edge. Middle labels stay centered.
          const position = isFirst
            ? 'left-0'
            : isLast
            ? 'right-0'
            : 'left-1/2 -translate-x-1/2'

          return (
            <Fragment key={s}>
              <div className="relative w-7 shrink-0">
                <span className={`absolute top-0 ${position} text-xs font-medium whitespace-nowrap ${isCurrent ? 'text-gray-900' : 'text-gray-500'}`}>
                  {statusLabel(s)}
                </span>
              </div>
              {!isLast && <div className="flex-1 mx-1" />}
            </Fragment>
          )
        })}
      </div>

      <div className="flex items-center gap-3 mt-4">
        {isFinal ? (
          <span className="text-sm font-medium text-emerald-600 inline-flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            Completed
          </span>
        ) : (
          <button
            type="button"
            onClick={() => nextStatus && onChange(nextStatus)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 transition-opacity"
          >
            Mark as {nextStatus && statusLabel(nextStatus)}
          </button>
        )}

        {prevStatus && (
          <button
            type="button"
            onClick={() => onChange(prevStatus)}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Move back to {statusLabel(prevStatus)}
          </button>
        )}
      </div>
    </div>
  )
}
